import React, { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { MailIcon, PhoneIcon } from 'lucide-react';
import { navItems, site } from '../../data/site';
import { ButtonLink } from '../ui/ButtonLink';
interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
  activeId: string | null;
  toggleRef: RefObject<HTMLButtonElement>;
}
export function MobileMenu({
  open,
  onClose,
  activeId,
  toggleRef
}: MobileMenuProps) {
  const firstLinkRef = useRef<HTMLAnchorElement>(null);
  const reduce = useReducedMotion();
  useEffect(() => {
    if (!open) return;
    firstLinkRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        toggleRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose, toggleRef]);
  return <AnimatePresence data-id="element-350" data-mp="53c2b1ze3onr">
      {open && <motion.div id="mobile-menu" initial={reduce ? false : {
      opacity: 0,
      y: -8
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={reduce ? {
      opacity: 0
    } : {
      opacity: 0,
      y: -8
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} className="absolute inset-x-0 top-full max-h-[calc(100dvh-76px)] overflow-y-auto border-t border-white/10 bg-ink lg:hidden" data-id="element-351" data-mp="9vwpvlrpd5iu">
          <nav aria-label="Mobile" className="px-5 pb-8 pt-4 sm:px-8" data-id="element-352" data-mp="3mcsb2wypzl99">
            <ul className="divide-y divide-white/10" data-id="element-353" data-mp="3rd68zm6lxetk">
              {navItems.map((item, i) => <li key={item.id} data-id="element-354" data-mp="11o46wnb4ejxr" data-mpi={i}>
                  <a ref={i === 0 ? firstLinkRef : undefined} href={`#${item.id}`} onClick={onClose} aria-current={activeId === item.id ? 'true' : undefined} className={`flex min-h-[56px] items-center font-display text-[1.9rem] font-semibold uppercase tracking-wide transition-colors duration-150 ${activeId === item.id ? 'text-white' : 'text-white/75 hover:text-white'}`} data-id="element-355" data-mp="11mt1kj21144j">
                    {activeId === item.id && <span className="mr-3 h-2 w-2 rounded-full bg-sun" aria-hidden="true" data-id="element-356" data-mp="2jyax3zz3wdwb" />}
                    {item.label}
                  </a>
                </li>)}
            </ul>
            <ButtonLink href="#first-class" onClick={onClose} className="mt-6 w-full" data-id="element-357" data-mp="2tp5k56k6fhit">
              Book a Free Trial Class
            </ButtonLink>
            <div className="mt-6 flex flex-col gap-1 text-[15px] text-white/75" data-id="element-358" data-mp="2ow2monca3nxc">
              <a href={site.phoneHref} className="inline-flex min-h-[44px] items-center gap-3 hover:text-white" data-id="element-359" data-mp="21709psslaj96">
                <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-360" data-mp="1w7dvbg10ywys" /> {site.phoneDisplay}
              </a>
              <a href={site.emailHref} className="inline-flex min-h-[44px] items-center gap-3 hover:text-white" data-id="element-361" data-mp="bpcsskeoq1q4">
                <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-362" data-mp="2qrdqz8f94u3" /> {site.email}
              </a>
            </div>
          </nav>
        </motion.div>}
    </AnimatePresence>;
}